import ApiWithLogging from '../utils/apiWithLogging';

export interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
  relatedEntityType?: string; // สัญญา, อุปกรณ์, ซิม, etc.
  relatedEntityId?: number; // ID ของ entity ที่เกี่ยวข้อง
  /** payload ของแจ้งเตือนจาก backend (เช่น quotation_number / job_number) — ใช้แสดงเลขที่เอกสารแทน id ภายใน */
  data?: Record<string, unknown> | null;
  userId: number;
}

export interface NotificationSettings {
  line_configured: boolean;
  email_configured: boolean;
  telegram_configured: boolean;
}

export interface NotificationForm {
  type: 'line' | 'email' | 'telegram';
  message: string;
  to_email?: string;
  subject?: string;
}

export interface NotificationChannel {
  id: string;
  name: string;
  enabled: boolean;
  recipients?: string[];
  token?: string;
  botToken?: string;
  chatId?: string;
}

export interface NotificationType {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  advanceDays?: number[];
  afterDays?: number[];
  threshold?: number;
  channels: string[];
  recipients: {
    roles: string[];
    users: string[];
  };
}

// สำหรับข้อมูลการตอบกลับจาก API
interface ApiResponse<T> {
  items?: T[];
  total?: number;
  success?: boolean;
  message?: string;
  data?: T;
  unread_count?: number;
}

// สำหรับพารามิเตอร์ในการค้นหา
interface GetNotificationsParams {
  page?: number;
  limit?: number;
  search?: string;
  isRead?: boolean;
  unread_only?: boolean;
  type?: string;
  fromDate?: string;
  toDate?: string;
}

const notificationService = {
  // ดึงรายการแจ้งเตือนทั้งหมด
  // ApiWithLogging.get() returns response.data directly, so no need to access .data again
  getNotifications: async (params: GetNotificationsParams = {}): Promise<ApiResponse<Notification>> => {
    try {
      const data = await ApiWithLogging.get<ApiResponse<Notification>>('/notifications/', { params });
      // Map snake_case fields from API to camelCase for frontend
      if (data?.items) {
        data.items = data.items.map((item: any) => ({
          ...item,
          isRead: item.isRead ?? item.is_read ?? false,
          createdAt: item.createdAt ?? item.created_at,
          updatedAt: item.updatedAt ?? item.updated_at,
          relatedEntityType: item.relatedEntityType ?? item.reference_type,
          relatedEntityId: item.relatedEntityId ?? item.reference_id,
          userId: item.userId ?? item.user_id,
        }));
      }
      return data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  // ดึงรายละเอียดการแจ้งเตือน
  getNotificationById: async (id: number): Promise<Notification> => {
    try {
      const data = await ApiWithLogging.get<Notification>(`/notifications/${id}`);
      return data;
    } catch (error) {
      console.error(`Error fetching notification with id ${id}:`, error);
      throw error;
    }
  },

  // อ่านการแจ้งเตือน
  markAsRead: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.put<ApiResponse<void>>(`/notifications/${id}/read`);
      return data;
    } catch (error) {
      console.error(`Error marking notification ${id} as read:`, error);
      throw error;
    }
  },

  // อ่านการแจ้งเตือนทั้งหมด
  markAllAsRead: async (): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.put<ApiResponse<void>>('/notifications/read-all');
      return data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  // ลบการแจ้งเตือนทั้งหมด (ตามสิทธิ์ — backend scope เอง)
  clearAll: async (): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.delete<ApiResponse<void>>('/notifications/clear-all');
      return data;
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  },

  // ลบการแจ้งเตือน
  deleteNotification: async (id: number): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.delete<ApiResponse<void>>(`/notifications/${id}`);
      return data;
    } catch (error) {
      console.error(`Error deleting notification ${id}:`, error);
      throw error;
    }
  },

  // ส่งการแจ้งเตือนแบบ manual (backend expects query params)
  sendManualNotification: async (data: NotificationForm): Promise<ApiResponse<void>> => {
    try {
      const params = new URLSearchParams();
      params.append('notification_type', data.type);
      params.append('message', data.message);
      if (data.to_email) params.append('to_email', data.to_email);
      if (data.subject) params.append('subject', data.subject);
      const response = await ApiWithLogging.post<ApiResponse<void>>(`/notifications/send?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Error sending manual notification:', error);
      throw error;
    }
  },

  // ทดสอบ LINE Notification
  testLineNotification: async (message: string): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.post<ApiResponse<void>>('/notifications/test-line', { message });
      return data;
    } catch (error) {
      console.error('Error testing LINE notification:', error);
      throw error;
    }
  },

  // ทดสอบ Email Notification
  testEmailNotification: async (to_email: string, subject: string, body: string): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.post<ApiResponse<void>>('/notifications/test-email', { to_email, subject, body });
      return data;
    } catch (error) {
      console.error('Error testing email notification:', error);
      throw error;
    }
  },

  // ทดสอบ Telegram Notification
  testTelegramNotification: async (message: string): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.post<ApiResponse<void>>('/notifications/test-telegram', { message });
      return data;
    } catch (error) {
      console.error('Error testing Telegram notification:', error);
      throw error;
    }
  },

  // ตรวจสอบรายการสัญญาที่หมดอายุแบบ manual
  checkExpiredContracts: async (): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.post<ApiResponse<void>>('/notifications/check-expired-contracts');
      return data;
    } catch (error) {
      console.error('Error checking expired contracts:', error);
      throw error;
    }
  },

  // ตรวจสอบรายการสัญญาที่ใกล้หมดอายุแบบ manual
  checkExpiringContracts: async (): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.post<ApiResponse<void>>('/notifications/check-expiring-contracts');
      return data;
    } catch (error) {
      console.error('Error checking expiring contracts:', error);
      throw error;
    }
  },

  // ส่งการแจ้งเตือนกำหนดคืนอุปกรณ์แบบ manual
  sendRentalReminders: async (): Promise<ApiResponse<void>> => {
    try {
      const data = await ApiWithLogging.post<ApiResponse<void>>('/notifications/send-rental-reminders');
      return data;
    } catch (error) {
      console.error('Error sending rental reminders:', error);
      throw error;
    }
  },

  // ดึงการตั้งค่าการแจ้งเตือน
  getNotificationConfig: async (): Promise<NotificationSettings> => {
    try {
      const data = await ApiWithLogging.get<NotificationSettings>('/notifications/config');
      return data;
    } catch (error) {
      console.error('Error fetching notification config:', error);
      throw error;
    }
  },
};

export default notificationService;